import React from 'react';
import styled from 'styled-components';
import { Rnd } from 'react-rnd';
import PlaceholderChip from './PlaceholderChip';
import {
  fieldId,
  fieldLabel,
  printedLabel,
  MIN_CHIP_H,
  MIN_CHIP_W,
} from './placeholderFields';
import { VIEW_HEIGHT, VIEW_WIDTH } from '../../utils/formSetupCoords';

interface Props {
  fields: any[];
  imageSrc: string;
  zoom: number;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** Geometry change for one field, in view pixels. */
  onMove: (field: any, patch: { x?: number; y?: number; width?: number; height?: number }) => void;
  onDelete: (field: any) => void;
  /** Fires once the form image reports its natural size. */
  onImageLoad: (naturalWidth: number) => void;
  /**
   * Size, in view pixels, that placeholder values will print at.
   *
   * The canvas is 72dpi, so a view pixel IS a point, and the server prints at
   * `font_size / scale` points. Passing it through means the chip shows the
   * real thing: what the label looks like on screen is what lands on the page.
   */
  labelFontPx: number;
  /**
   * The same thing for ONE field, when it carries its own font size.
   *
   * Optional so the prop can be left off entirely and every chip falls back to
   * labelFontPx — the behaviour before per-placeholder sizes existed.
   */
  fieldFontPx?: (field: any) => number;
  /** Optional react-pdf document, used by the create flow before a file exists. */
  children?: React.ReactNode;
}

// The drag surface for Form Setup placeholders. Modelled on the Inspection Form
// Builder's FormBuilderCanvas: controlled <Rnd> per field, zoom as a CSS
// transform on the stage, and geometry stored only in the parent.
//
// Deliberately WITHOUT the builder's snap guides — not requested here, and they
// are the fiddliest part of that component.
const PlaceholderCanvas = ({
  fields,
  imageSrc,
  zoom,
  selectedId,
  onSelect,
  onMove,
  onDelete,
  onImageLoad,
  labelFontPx,
  fieldFontPx,
  children = null,
}: Props) => {
  const stageRef = React.useRef<HTMLDivElement>(null);

  return (
    <Scroll>
      <StageOuter style={{ width: VIEW_WIDTH * zoom, height: VIEW_HEIGHT * zoom }}>
        <Stage
          ref={stageRef}
          onMouseDown={e => {
            // Clicking the bare page clears the selection.
            if (e.target === stageRef.current) onSelect(null);
          }}
          style={{
            width: VIEW_WIDTH,
            height: VIEW_HEIGHT,
            transform: `scale(${zoom})`,
          }}
        >
          {children}
          {imageSrc && (
            <PageImg
              src={imageSrc}
              alt="Form"
              draggable={false}
              onLoad={e => onImageLoad((e.target as HTMLImageElement).naturalWidth)}
            />
          )}

          {fields.map(field => {
            const id = fieldId(field);
            const isSelected = id === selectedId;
            return (
              <Rnd
                key={id}
                bounds="parent"
                // Without this the pointer delta is not divided by the CSS
                // transform and the chip runs away from the cursor when zoomed.
                scale={zoom}
                minWidth={MIN_CHIP_W}
                minHeight={MIN_CHIP_H}
                size={{ width: Number(field.width), height: Number(field.height) }}
                position={{ x: Number(field.x), y: Number(field.y) }}
                onDragStart={() => onSelect(id)}
                onDragStop={(_e, d) => onMove(field, { x: Math.round(d.x), y: Math.round(d.y) })}
                onResizeStart={() => onSelect(id)}
                onResizeStop={(_e, _dir, ref, _delta, position) =>
                  onMove(field, {
                    x: Math.round(position.x),
                    y: Math.round(position.y),
                    width: parseInt(ref.style.width, 10),
                    height: parseInt(ref.style.height, 10),
                  })
                }
                style={{ zIndex: isSelected ? 2 : 1 }}
              >
                <ChipHost
                  role="button"
                  tabIndex={-1}
                  onMouseDown={() => onSelect(id)}
                >
                  <PlaceholderChip
                    label={printedLabel(field)}
                    title={fieldLabel(field)}
                    selected={isSelected}
                    onDelete={() => onDelete(field)}
                    fontPx={
                      fieldFontPx ? fieldFontPx(field) : labelFontPx
                    }
                  />
                </ChipHost>
              </Rnd>
            );
          })}
        </Stage>
      </StageOuter>
    </Scroll>
  );
};

export default PlaceholderCanvas;

const Scroll = styled.div`
  flex: 1;
  min-width: 0;
  max-height: 72vh;
  overflow: auto;
  background: var(--neutral-100);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 16px;
  box-sizing: border-box;
`;

const StageOuter = styled.div`
  position: relative;
  /* margin auto centres the page when it fits and degrades to top-left aligned
     (fully scrollable) when zoomed past the viewport — unlike flex centring,
     which makes the overflow unreachable. */
  margin: auto;
`;

const Stage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: top left;
  background: var(--neutral-0);
  box-shadow: var(--shadow-xs);

  .annotationLayer {
    display: none;
  }
`;

const PageImg = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /* Must stay "fill": the stored coordinates are a plain linear scale of the
     source image, so letterboxing would shift every placeholder. */
  object-fit: fill;
  pointer-events: none;
  user-select: none;
`;

const ChipHost = styled.div`
  width: 100%;
  height: 100%;
`;
